import { useState, useRef, useEffect } from 'react';
import {
  Pencil, Share2, ChevronDown, Link2,
  LayoutGrid, Activity, Sigma, FolderOpen,
  Map, Gauge, Move, Timer, Table2, CircleDot, ChartScatter, Flag, Zap,
  MessageSquare, Sparkles, Film,
  Cable, Users, Box, CloudUpload,
  FileDown, List, Trash2, ZoomOut,
  Plus, MoreHorizontal,
  Sun, Cloud, CloudRain, CloudSun,
} from 'lucide-react';
import styles from './SessionHeader.module.css';
import BetaBadge from '../common/BetaBadge';
import { convertWeatherUnits } from '../../utils/unitConvert';
import { downloadSessionFile } from '../../services/api';

// Shared outside-click dismissal for all header dropdowns
function useDismiss(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, setOpen]);
  return ref;
}

// Inline-editable per-session label (e.g. "Novice 1"). Distinct from the track
// name, which is read-only. Empty shows a muted "Name this session" placeholder.
function EditableLabel({ label, onSave, placeholder = 'Name this session' }) {
  const [editing, setEditing] = useState(false);
  const [value,   setValue]   = useState(label || '');
  const [saving,  setSaving]  = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setValue(label || ''); }, [label]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const commit = async () => {
    const trimmed = value.trim();
    if (trimmed === (label || '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter')  { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { setValue(label || ''); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={styles.nameInput}
        value={value}
        disabled={saving}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        size={Math.max((value || placeholder).length + 2, 12)}
      />
    );
  }

  return (
    <button
      className={styles.nameBtn}
      onClick={() => { setValue(label || ''); setEditing(true); }}
      title={label ? `Rename this session: ${label}` : 'Name this session'}
    >
      <span className={label ? styles.label : styles.labelPlaceholder}>{label || placeholder}</span>
      <span className={styles.editHint}><Pencil size={11} /></span>
    </button>
  );
}

function WeatherGlyph({ condition }) {
  const c = (condition || '').toLowerCase();
  const Icon = c.includes('rain')                                ? CloudRain
             : c.includes('overcast') || c.includes('mostly cloudy') ? Cloud
             : c.includes('partly')                              ? CloudSun
             : Sun;
  return <Icon size={13} />;
}

// ── View menu — analysis panels + layout presets ─────────────────────────────
const LAYOUT_PRESETS = [
  { key: 'standard',      label: 'Standard',        desc: 'Speed · G-Force · 3D View · Map' },
  { key: 'delta',         label: 'Delta',           desc: 'Speed · Time Delta · 3D View · Map' },
  { key: 'friction',      label: 'Friction',        desc: 'Speed · Time Delta · Map · Friction Circle' },
  { key: 'videoStandard', label: 'Video Standard',  desc: 'Speed · G-Force · Video · Map' },
  { key: 'videoDelta',    label: 'Video Delta',     desc: 'Speed · Time Delta · Video · Map' },
];

function ViewMenu({
  showMap, onToggleMap,
  showSpeedTrace, onToggleSpeedTrace,
  showGTrace, onToggleGTrace,
  showDeltaTrace, onToggleDeltaTrace,
  showMetrics, onToggleMetrics,
  showFrictionCircle, onToggleFrictionCircle,
  showScatterPlot, onToggleScatterPlot,
  showSplits, onToggleSplits,
  showPowerTrace, onTogglePowerTrace,
  showHeadToHead, onToggleHeadToHead,
  showTrackDifference, onToggleTrackDifference,
  showLap3D, onToggleLap3D, canLap3D = false,
  showComments, onToggleComments,
  showPromptPanel, onTogglePrompt,
  enableAi = true,
  hasVideo, showVideo, onToggleVideo,
  onResetLayout,
}) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);

  const panels = [
    { label: 'Map',             Icon: Map,          active: showMap,            onToggle: onToggleMap },
    { label: 'Speed',           Icon: Gauge,        active: showSpeedTrace,     onToggle: onToggleSpeedTrace },
    { label: 'G-Force',         Icon: Move,         active: showGTrace,         onToggle: onToggleGTrace },
    { label: 'Time Delta',      Icon: Timer,        active: showDeltaTrace,     onToggle: onToggleDeltaTrace },
    { label: 'Corner Metrics',  Icon: Table2,       active: showMetrics,        onToggle: onToggleMetrics },
    { label: 'Head-to-Head',    Icon: Users,        active: showHeadToHead,     onToggle: onToggleHeadToHead },
    { label: 'Track Difference', Icon: Activity,    active: showTrackDifference, onToggle: onToggleTrackDifference },
    // Hidden rather than disabled with no laps to race: the panel would open on
    // an empty stage, which reads as broken rather than as "pick some laps".
    ...(canLap3D ? [{ label: '3D View', Icon: Box, active: showLap3D, onToggle: onToggleLap3D }] : []),
    { label: 'Friction Circle', Icon: CircleDot,    active: showFrictionCircle, onToggle: onToggleFrictionCircle },
    { label: 'Scatter Plot',    Icon: ChartScatter, active: showScatterPlot,    onToggle: onToggleScatterPlot },
    { label: 'Split Times',     Icon: Flag,         active: showSplits,         onToggle: onToggleSplits },
    { label: 'Power',           Icon: Zap,          active: showPowerTrace,     onToggle: onTogglePowerTrace },
    { label: 'Comments',        Icon: MessageSquare, active: showComments,      onToggle: onToggleComments },
    ...(enableAi  ? [{ label: 'AI Coach', Icon: Sparkles, active: showPromptPanel, onToggle: onTogglePrompt }] : []),
    ...(hasVideo  ? [{ label: 'Video',    Icon: Film,     active: showVideo,       onToggle: onToggleVideo }]  : []),
  ];

  const activeCount = panels.filter(p => p.active).length;

  const applyLayout = (key) => {
    if (window.confirm('Reset layout to ' + LAYOUT_PRESETS.find(p => p.key === key).label + '?')) {
      onResetLayout(key);
    }
    setOpen(false);
  };

  return (
    <div className={styles.panelPicker} ref={ref}>
      <button
        className={`${styles.viewBtn} ${activeCount > 0 ? styles.viewActive : ''}`}
        onClick={() => setOpen(v => !v)}
        title="Panels and layout"
        aria-label={`View: ${activeCount} panels visible`}
      >
        <LayoutGrid size={14} />
        <span className={styles.viewBtnLabel}>View{activeCount > 0 ? ` (${activeCount})` : ''}</span>
        <ChevronDown size={12} className={styles.caret} />
      </button>
      {open && (
        <div className={`${styles.panelDropdown} ${styles.dropRight}`}>
          <div className={styles.panelDropdownHdr}>Analysis Panels</div>
          <div className={styles.panelScroll}>
            {panels.map(({ label, Icon, active, onToggle }) => (
              <label key={label} className={`${styles.panelItem} ${active ? styles.panelItemActive : ''}`}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={onToggle}
                />
                <Icon size={13} className={styles.itemIcon} />
                <span className={styles.panelItemLabel}>{label}</span>
              </label>
            ))}
          </div>
          {onResetLayout && (
            <>
              <div className={styles.panelDropdownHdr}>Layout Presets</div>
              {LAYOUT_PRESETS.map(({ key, label, desc }) => (
                <div key={key} className={styles.panelItem} onClick={() => applyLayout(key)}>
                  <span className={styles.panelItemLabel}>
                    {label}
                    <span className={styles.itemDesc}>{desc}</span>
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ChannelPicker({ availableChannels, selectedChannels, onChannelToggle, valueChannels = [], onValueToggle }) {
  const [open,   setOpen]   = useState(false);
  const [filter, setFilter] = useState('');
  const ref      = useDismiss(open, setOpen);
  const inputRef = useRef(null);

  // Focus filter input when dropdown opens
  useEffect(() => {
    if (open) {
      setFilter('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const count   = selectedChannels.length;
  const needle  = filter.trim().toLowerCase();
  const visible = needle
    ? (availableChannels || []).filter(ch => ch.name.toLowerCase().includes(needle))
    : (availableChannels || []);

  return (
    <div className={styles.channelPicker} ref={ref}>
      <button
        className={`${styles.viewBtn} ${count > 0 ? styles.viewActive : ''}`}
        onClick={() => setOpen(v => !v)}
        title="Pick channels to trace"
        aria-label={`Channels: ${count} selected`}
      >
        <Activity size={14} />
        <span className={styles.viewBtnLabel}>Channels{count > 0 ? ` (${count})` : ''}</span>
        <ChevronDown size={12} className={styles.caret} />
      </button>
      {open && (
        <div className={styles.channelDropdown}>
          <div className={styles.channelDropdownHdr}>
            <span>Plot</span> up to 8 · <span>Value</span> readouts
          </div>
          <div className={styles.channelFilterWrap}>
            <input
              ref={inputRef}
              className={styles.channelFilter}
              type="text"
              placeholder="Filter channels…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && (filter ? setFilter('') : setOpen(false))}
            />
          </div>
          <div className={styles.channelList}>
            {visible.length === 0 && (
              <div className={styles.channelNoMatch}>No channels match</div>
            )}
            {visible.map(ch => {
              const checked  = selectedChannels.some(s => s.key === ch.key);
              const disabled = !checked && count >= 8;
              const valChecked = valueChannels.some(s => s.key === ch.key);
              const isPrimary = ch.source === 'primary';
              return (
                <div key={ch.key} className={styles.channelItem}>
                  <label className={`${styles.channelPick} ${disabled ? styles.channelItemDisabled : ''}`} title="Show as plot">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => onChannelToggle(ch)}
                    />
                    <span className={styles.channelItemName}>{ch.name}</span>
                    {ch.unit && <span className={styles.channelItemUnit}>{ch.unit}</span>}
                    {!isPrimary && <span className={styles.channelDriverTag}>{ch.driverLabel}</span>}
                  </label>
                  {isPrimary && (
                    <button
                      type="button"
                      className={`${styles.channelValBtn} ${valChecked ? styles.channelValOn : ''}`}
                      onClick={() => onValueToggle?.(ch)}
                      title="Show value readout"
                    >
                      Value
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FormulaPicker({ formulas, panelVisibility, onToggle, onNew, onEdit }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);

  const activeCount = formulas.filter(f => panelVisibility[`showFormula_${f.id}`]).length;

  return (
    <div className={styles.channelPicker} ref={ref}>
      <button
        className={`${styles.viewBtn} ${activeCount > 0 ? styles.viewActive : ''}`}
        onClick={() => setOpen(v => !v)}
        title="Manage formula panels"
        aria-label={`Formulas: ${activeCount} visible`}
      >
        <Sigma size={14} />
        <span className={styles.viewBtnLabel}>Formulas{activeCount > 0 ? ` (${activeCount})` : ''}</span>
        <ChevronDown size={12} className={styles.caret} />
      </button>
      {open && (
        <div className={styles.channelDropdown}>
          <div className={styles.channelDropdownHdr}>Formula Panels</div>
          <div className={styles.channelList}>
            {(() => {
              const builtins = formulas.filter(f => f.builtin);
              const userFormulas = formulas.filter(f => !f.builtin);
              const renderRow = (f) => {
                const active = !!panelVisibility[`showFormula_${f.id}`];
                // Hover tooltip: what it shows + the underlying expression.
                const tooltip = [f.description, f.expression && `Formula: ${f.expression}`]
                  .filter(Boolean).join('\n\n');
                return (
                  <label key={f.id} className={styles.channelItem} title={tooltip || undefined}
                         style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => onToggle(f, active)}
                    />
                    {f.color && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                    )}
                    <span className={styles.channelItemName} style={{ flex: 1 }}>{f.name}</span>
                    {f.unit && <span className={styles.channelItemUnit}>{f.unit}</span>}
                    {!f.builtin && (
                      <button
                        onClick={e => { e.preventDefault(); e.stopPropagation(); onEdit(f); setOpen(false); }}
                        className={styles.formulaEditBtn}
                        title="Edit formula"
                      ><Pencil size={12} /></button>
                    )}
                  </label>
                );
              };
              return (
                <>
                  {builtins.length > 0 && (
                    <div className={styles.channelDropdownHdr}>Built-in</div>
                  )}
                  {builtins.map(renderRow)}
                  {userFormulas.length > 0 && (
                    <div className={styles.channelDropdownHdr}>Your formulas</div>
                  )}
                  {userFormulas.map(renderRow)}
                  {userFormulas.length === 0 && (
                    <div className={styles.channelNoMatch}>No custom formulas yet</div>
                  )}
                </>
              );
            })()}
          </div>
          <button
            className={styles.newFormulaBtn}
            onClick={() => { onNew(); setOpen(false); }}
          >
            <Plus size={13} /> New Formula
          </button>
        </div>
      )}
    </div>
  );
}

function ArrangePanelsButton({ onArrangePanels }) {
  if (!onArrangePanels) return null;

  return (
    <button
      className={styles.viewBtn}
      onClick={onArrangePanels}
      title="Arrange all open panels in the visible workspace"
      aria-label="Arrange open panels"
    >
      <Move size={14} />
      <span className={styles.viewBtnLabel}>Arrange</span>
    </button>
  );
}

function AiDebriefButton({ enabled, status, onOpen, collapsed = false }) {
  if (!enabled || !onOpen) return null;
  const busy = status === 'building' || status === 'streaming';
  const ready = status === 'done';
  const failed = status === 'error';
  const title = busy ? 'AI Debrief is generating' : ready ? 'Open completed AI Debrief'
    : failed ? 'Open AI Debrief and try again' : 'Generate AI Debrief';

  return (
    <button
      className={`${styles.aiDebriefBtn} ${busy ? styles.aiDebriefBusy : ''} ${ready ? styles.aiDebriefReady : ''}`}
      onClick={onOpen}
      title={title}
      aria-label={collapsed ? title : undefined}
    >
      <span className={styles.aiDebriefIcon} aria-hidden="true">
        <Sparkles size={15} />
        <i />
      </span>
      <span className={styles.aiDebriefLabel}>AI Debrief</span>
      <span className={styles.aiDebriefSignal} aria-hidden="true" />
    </button>
  );
}

// ── Session menu — share / downloads / list / delete ─────────────────────────
function SessionMenu({
  sessionId, downloadMeta,
  onShare, isShared,
  onCopyViewLink,
  onServerSync,
  onReset, onDeleteSession,
  publicMode, guestMode,
  onAddEcuData, onAddVideo, addVideoLabel = 'Add Video',
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const ref = useDismiss(open, setOpen);

  const handleCopyLink = () => {
    onCopyViewLink();
    setLinkCopied(true);
    setTimeout(() => { setLinkCopied(false); setOpen(false); }, 900);
  };

  const handleDownload = async (type, filename) => {
    if (downloadingId) return;
    setDownloadingId(type);
    try {
      await downloadSessionFile(sessionId, type, filename);
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const dm = downloadMeta || {};
  const showDownloads = (!publicMode || guestMode);
  const originalIsXrk = /\.xr[zk]$/i.test(dm.originalFilename || '');
  const downloads = showDownloads ? [
    dm.hasOriginal && { type: 'original', label: 'Original File', filename: dm.originalFilename },
    dm.canReload   && { type: 'csv',      label: 'Processed CSV' },
    dm.canReload && !originalIsXrk && { type: 'xrk', label: 'Export to RaceStudio3' },
    dm.hasRendered && { type: 'rendered', label: 'Rendered Video' },
    dm.hasMp4      && { type: 'video',    label: 'MP4 Video' },
    dm.hasMov      && { type: 'video_original', label: 'Original Video (MOV)' },
  ].filter(Boolean) : [];

  const hasShare   = !!onShare;
  const hasList    = !publicMode;
  const hasServerSync = !!onServerSync;
  const hasDelete  = !!onDeleteSession;

  const hasAdd = !!onAddVideo || !!onAddEcuData;

  if (!hasAdd && !hasShare && !downloads.length && !hasList && !hasServerSync && !hasDelete) return null;

  return (
    <div className={styles.panelPicker} ref={ref}>
      <button
        className={`${styles.viewBtn} ${compact ? styles.moreBtn : ''}`}
        onClick={() => setOpen(v => !v)}
        title={compact ? 'More analysis and session actions' : 'Share, download, navigate or delete this session'}
        aria-label={compact ? 'More analysis and session actions' : undefined}
      >
        {compact ? <MoreHorizontal size={14} /> : <FolderOpen size={14} />}
        <span className={styles.viewBtnLabel}>{compact ? 'More' : 'Session'}</span>
        <ChevronDown size={12} className={styles.caret} />
      </button>
      {open && (
        <div className={`${styles.panelDropdown} ${styles.dropRight}`}>
          {onAddVideo && (
            <div className={styles.panelItem} onClick={() => { setOpen(false); onAddVideo(); }}>
              <Film size={13} className={styles.itemIcon} />
              <span className={styles.panelItemLabel}>{addVideoLabel}</span>
            </div>
          )}
          {onAddEcuData && (
            <div className={styles.panelItem} onClick={() => { setOpen(false); onAddEcuData(); }}>
              <Cable size={13} className={styles.itemIcon} />
              <span className={styles.panelItemLabel}>Add ECU Data</span>
            </div>
          )}
          {hasAdd && (hasShare || downloads.length > 0 || hasList || hasServerSync || hasDelete) && (
            <div className={styles.panelDivider} />
          )}
          {hasShare && (
            <div className={styles.panelItem} onClick={() => { setOpen(false); onShare(); }}>
              <Share2 size={13} className={`${styles.itemIcon} ${isShared ? styles.sharedIcon : ''}`} />
              <span className={styles.panelItemLabel}>
                {isShared ? 'Manage sharing' : 'Share session'}
                {isShared && <span className={styles.itemDesc}>Currently shared</span>}
              </span>
            </div>
          )}
          {onCopyViewLink && (
            <div className={styles.panelItem} onClick={handleCopyLink}>
              <Link2 size={13} className={`${styles.itemIcon} ${linkCopied ? styles.sharedIcon : ''}`} />
              <span className={styles.panelItemLabel}>
                {linkCopied ? 'Link copied ✓' : 'Copy view link'}
                <span className={styles.itemDesc}>Restores these sessions + selected laps</span>
              </span>
            </div>
          )}
          {downloads.length > 0 && (
            <>
              {hasShare && <div className={styles.panelDivider} />}
              <div className={styles.panelDropdownHdr}>Download</div>
              {downloads.map(({ type, label, filename }) => (
                <div
                  key={type}
                  className={styles.panelItem}
                  onClick={() => {
                    if (type === 'rendered') {
                      setOpen(false);
                      window.location.href = `/api/rendered_video/${sessionId}?download=1`;
                    } else {
                      handleDownload(type, filename);
                    }
                  }}
                >
                  <FileDown size={13} className={styles.itemIcon} />
                  <span className={styles.panelItemLabel}>
                    {downloadingId === type ? `${label}…` : label}
                  </span>
                </div>
              ))}
            </>
          )}
          {(hasList || hasServerSync) && (
            <>
              <div className={styles.panelDivider} />
              {hasList && (
                <div className={styles.panelItem} onClick={() => { setOpen(false); onReset(); }}>
                  <List size={13} className={styles.itemIcon} />
                  <span className={styles.panelItemLabel}>Session List</span>
                </div>
              )}
              {hasServerSync && (
                <div className={styles.panelItem} onClick={() => { setOpen(false); onServerSync(); }}>
                  <CloudUpload size={13} className={styles.itemIcon} />
                  <span className={styles.panelItemLabel}>Server Sync…</span>
                </div>
              )}
            </>
          )}
          {hasDelete && (
            <>
              <div className={styles.panelDivider} />
              <div
                className={`${styles.panelItem} ${styles.dangerItem}`}
                onClick={() => { setOpen(false); onDeleteSession(); }}
              >
                <Trash2 size={13} className={styles.itemIcon} />
                <span className={styles.panelItemLabel}>Delete Session</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function AnalysisToolbar({
  showMap,           onToggleMap,
  showSpeedTrace,    onToggleSpeedTrace,
  showGTrace,        onToggleGTrace,
  showDeltaTrace,    onToggleDeltaTrace,
  availableChannels, selectedChannels, onChannelToggle,
  valueChannels = [], onValueToggle,
  onAddEcuData,
  onAddVideo, addVideoLabel,
  onShare, isShared = false,
  onCopyViewLink,
  onServerSync,
  hasVideo,          showVideo, onToggleVideo,
  showMetrics,       onToggleMetrics,
  showFrictionCircle, onToggleFrictionCircle,
  showScatterPlot,    onToggleScatterPlot,
  showSplits,         onToggleSplits,
  showPowerTrace,     onTogglePowerTrace,
  showHeadToHead,     onToggleHeadToHead,
  showTrackDifference, onToggleTrackDifference,
  showLap3D,          onToggleLap3D, canLap3D,
  showPromptPanel,    onTogglePrompt,
  enableAi,
  showComments, onToggleComments,
  recapEnabled = false, recapStatus = null, onOpenRecap,
  formulas, panelVisibility,
  onFormulaToggle, onFormulaNew, onFormulaEdit,
  onResetLayout, onArrangePanels,
  onOpenStudio,
  sessionId, downloadMeta,
  onReset, onDeleteSession,
  publicMode = false,
  guestMode = false,
  isMobile = false,
  collapsed = false,
}) {
  const recapButton = (
    <AiDebriefButton
      enabled={recapEnabled}
      status={recapStatus}
      onOpen={onOpenRecap}
      collapsed={collapsed}
    />
  );
  const viewMenu = !isMobile && (
    <ViewMenu
      showMap={showMap} onToggleMap={onToggleMap}
      showSpeedTrace={showSpeedTrace} onToggleSpeedTrace={onToggleSpeedTrace}
      showGTrace={showGTrace} onToggleGTrace={onToggleGTrace}
      showDeltaTrace={showDeltaTrace} onToggleDeltaTrace={onToggleDeltaTrace}
      showMetrics={showMetrics} onToggleMetrics={onToggleMetrics}
      showFrictionCircle={showFrictionCircle} onToggleFrictionCircle={onToggleFrictionCircle}
      showScatterPlot={showScatterPlot} onToggleScatterPlot={onToggleScatterPlot}
      showSplits={showSplits} onToggleSplits={onToggleSplits}
      showPowerTrace={showPowerTrace} onTogglePowerTrace={onTogglePowerTrace}
      showHeadToHead={showHeadToHead} onToggleHeadToHead={onToggleHeadToHead}
      showTrackDifference={showTrackDifference} onToggleTrackDifference={onToggleTrackDifference}
      showLap3D={showLap3D} onToggleLap3D={onToggleLap3D} canLap3D={canLap3D}
      showComments={showComments} onToggleComments={onToggleComments}
      showPromptPanel={showPromptPanel} onTogglePrompt={onTogglePrompt}
      enableAi={enableAi}
      hasVideo={hasVideo} showVideo={showVideo} onToggleVideo={onToggleVideo}
      onResetLayout={onResetLayout}
    />
  );
  const arrangeButton = !isMobile && (
    <ArrangePanelsButton onArrangePanels={onArrangePanels} />
  );

  // Hands the selected laps to Video Studio. A viewBtn so it collapses to its
  // icon with the rest of the toolbar in the compact/collapsed variants.
  const studioButton = onOpenStudio && !isMobile && (
    <button
      className={`${styles.viewBtn} ${styles.studioBtn}`}
      onClick={onOpenStudio}
      title="Open the selected laps in Video Studio (Beta)"
    >
      <Film size={13} />
      <span className={styles.viewBtnLabel}>Create in Video Studio<BetaBadge /></span>
    </button>
  );

  const channelMenu = (
    <ChannelPicker
      availableChannels={availableChannels}
      selectedChannels={selectedChannels}
      onChannelToggle={onChannelToggle}
      valueChannels={valueChannels}
      onValueToggle={onValueToggle}
    />
  );

  const formulaMenu = !publicMode && (
    <FormulaPicker
      formulas={formulas || []}
      panelVisibility={panelVisibility || {}}
      onToggle={onFormulaToggle}
      onNew={onFormulaNew}
      onEdit={onFormulaEdit}
    />
  );

  const sessionMenuProps = {
    sessionId, downloadMeta, onShare, isShared, onCopyViewLink,
    onServerSync,
    onReset, onDeleteSession, publicMode, guestMode,
    onAddEcuData, onAddVideo, addVideoLabel,
  };

  if (isMobile) {
    return (
      <div className={`${styles.analysisToolbar} ${styles.mobileToolbar}`}>
        {recapButton}
        {channelMenu}
        {formulaMenu}
        <SessionMenu {...sessionMenuProps} />
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className={`${styles.analysisToolbar} ${styles.collapsedToolbar}`}>
        {recapButton}
        {arrangeButton}
        {viewMenu}
        {channelMenu}
        {formulaMenu}
        {studioButton}
        <SessionMenu {...sessionMenuProps} />
      </div>
    );
  }

  return (
    <div className={styles.analysisToolbar}>
      <div className={styles.wideToolbar}>
        {recapButton}
        {arrangeButton}
        {viewMenu}
        {channelMenu}
        {formulaMenu}
        {studioButton}
        <SessionMenu {...sessionMenuProps} />
      </div>
      <div className={styles.compactToolbar}>
        {recapButton}
        {arrangeButton}
        {viewMenu}
        {channelMenu}
        {formulaMenu}
        {studioButton}
        <SessionMenu {...sessionMenuProps} compact />
      </div>
    </div>
  );
}

export function SessionMeta({
  meta, onRenameLabel,
  onShare, isShared = false,
  isZoomed, onResetZoom,
  unitPrefs,
  publicMode = false,
  inline = false,
}) {
  if (!meta) return null;

  // A session whose track was never identified has neither name, and the bare
  // fallback rendered an empty span between two separators — which reads as a
  // rendering fault rather than "we don't know". Match the sessions list, which
  // already substitutes "Unknown" server-side (api/sessions.py _row_to_session_dict).
  const trackLabel = meta.canonical_track_name || meta.session_name || 'Unknown';

  return (
    <div className={`${styles.left} ${inline ? styles.inlineMeta : ''}`}>
      {publicMode ? (
        meta.session_label && (
          <>
            <span className={styles.info} style={{ fontWeight: 600 }} title={meta.session_label}>{meta.session_label}</span>
            <span className={styles.sep}>·</span>
          </>
        )
      ) : (
        <>
          <EditableLabel
            label={meta.session_label}
            onSave={(newLabel) => onRenameLabel?.(newLabel)}
          />
          <span className={styles.sep}>·</span>
        </>
      )}
      <span className={styles.info} style={{ fontWeight: 600 }} title={trackLabel}>{trackLabel}</span>
      {onShare && isShared && (
        <button
          className={styles.sharedBadge}
          onClick={onShare}
          title="Shared — click to manage who has access"
        ><Share2 size={11} /> Shared</button>
      )}
      <span className={styles.sep}>·</span>
      <span className={styles.info} title={meta.vehicle}>{meta.vehicle}</span>
      <span className={styles.sep}>·</span>
      <span className={styles.info} title={meta.racer}>{meta.racer}</span>
      <span className={styles.sep}>·</span>
      <span className={styles.info} title={meta.date}>{meta.date}</span>
      {meta.weather && (() => {
        const prefs = unitPrefs || { temp: 'F', speed: 'mph', pressure: 'psi' };
        const w = convertWeatherUnits(meta.weather, prefs);
        const tooltipLines = [
          w.humidity  && `Humidity: ${w.humidity}`,
          w.wind      && `Wind: ${w.wind}${w.windDir ? ` ${w.windDir}` : ''}`,
          w.pressure  && `Pressure: ${w.pressure}`,
          meta.weather.precipitation_mm > 0 && `Precip: ${w.precip}`,
        ].filter(Boolean).join('\n');
        return (
          <>
            <span className={styles.sep}>·</span>
            <span className={styles.weatherPill} title={tooltipLines}>
              <span className={styles.weatherPillIcon}>
                <WeatherGlyph condition={meta.weather.condition} />
              </span>
              {w.temp}
              <span className={styles.weatherPillCond}>{w.condition}</span>
            </span>
          </>
        );
      })()}
      <span className={styles.sep}>·</span>
      <span className={styles.info}>{meta.sample_rate} Hz</span>
      {isZoomed && onResetZoom && (
        <button
          className={styles.unitsBtn}
          onClick={onResetZoom}
          title="Reset zoom on all traces"
          style={{ marginLeft: 6, color: '#f5c842' }}
        ><ZoomOut size={13} /> Reset Zoom</button>
      )}
    </div>
  );
}

export default function SessionHeader({
  meta, onRenameLabel,
  onShare, isShared = false,
  isZoomed, onResetZoom,
  unitPrefs,
  publicMode = false,
  mobileActions = null,
  // Mobile shell: stack metadata/buttons rows, wrap the button row, hide the
  // View menu (it manages desktop floating windows; mobile tabs show all
  // panels). Prop-driven so it always matches useIsMobile(), which also
  // triggers on coarse pointers at widths a CSS breakpoint would miss.
  isMobile = false,
}) {
  if (!meta) return null;

  return (
    <div className={`${styles.header} ${isMobile ? styles.mobileHeader : ''}`}>
      <SessionMeta
        meta={meta}
        onRenameLabel={onRenameLabel}
        onShare={onShare}
        isShared={isShared}
        isZoomed={isZoomed}
        onResetZoom={onResetZoom}
        unitPrefs={unitPrefs}
        publicMode={publicMode}
      />
      {mobileActions && <div className={styles.right}>{mobileActions}</div>}
    </div>
  );
}
